import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import InvoiceCreateDialog from "../components/invoices/InvoiceCreateDialog";
import InvoiceDetailDrawer from "../components/invoices/InvoiceDetailDrawer";
import { registerZeilen, useDeleteWebInvoiceDraft, useInvoiceRegister } from "../hooks/useInvoices";
import { useKunden, useProjekte } from "../hooks/usePlanung";
import {
  euro,
  belegArtLabel,
  KIND_LABEL,
  shortDate,
  STATUS_LABEL,
  statusTone,
  type InvoiceKind,
  type InvoiceRegisterItem,
  type RegisterFilter,
  type WebInvoiceDraftRecord,
} from "../lib/invoices";
import "../styles/invoices.css";
import { useIstTelefon } from "../hooks/useIstTelefon";

type Filter = RegisterFilter;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "quotes", label: "Angebote" },
  { value: "invoices", label: "Rechnungen" },
  { value: "draft", label: "Entwurf" },
  { value: "open", label: "Offen" },
  { value: "overdue", label: "Überfällig" },
  { value: "imported", label: "Importiert" },
];

const EMPTY_SUMMARY = {
  open_claims: 0, open_count: 0, overdue_claims: 0, overdue_count: 0,
  offers_outside: 0, offers_count: 0, paid_total: 0, paid_count: 0,
};

const isOverdue = (item: InvoiceRegisterItem) => item.kind !== "quote" && (
  item.status === "ueberfaellig" || (
    item.status === "gestellt" && Boolean(item.faellig_am) && item.faellig_am < new Date().toISOString().slice(0, 10)
  )
);

const isImported = (item: InvoiceRegisterItem) => (
  item.erzeugt_via === "external"
  || (item.erzeugt_via !== "web" && !item.yaml_pfad && !item.pdf_pfad)
);

/** Erst tippen lassen, dann fragen — sonst geht je Buchstabe eine Anfrage raus. */
function useVerzoegert(wert: string, ms = 250) {
  const [verzoegert, setVerzoegert] = useState(wert);
  useEffect(() => {
    const t = window.setTimeout(() => setVerzoegert(wert), ms);
    return () => window.clearTimeout(t);
  }, [wert, ms]);
  return verzoegert;
}

export default function RechnungenPage() {
  const navigate = useNavigate();
  const telefon = useIstTelefon();
  const { data: customers = [] } = useKunden();
  const { data: projects = [] } = useProjekte(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [createKind, setCreateKind] = useState<InvoiceKind | null>(null);
  const [selected, setSelected] = useState<InvoiceRegisterItem | null>(null);

  // Suche, Reiter und Kunde gehen zum Server — der durchsucht alle Belege
  // und liefert die erste Seite; weitere folgen beim Scrollen.
  const suche = useVerzoegert(search.trim());
  const kundeSuche = useVerzoegert(customerFilter.trim());
  const registerQuery = useInvoiceRegister({ q: suche, filter, kunde: kundeSuche });
  const erste = registerQuery.data?.pages[0];
  const filtered = registerZeilen(registerQuery.data);
  const webDrafts = erste?.drafts || [];
  const summary = erste?.summary || EMPTY_SUMMARY;
  const total = erste?.total ?? 0;
  const counts = { ...(erste?.counts || {}), draft: (erste?.counts?.draft || 0) + webDrafts.length } as Record<Filter, number>;

  // Nachladen, sobald die Fühlerzeile unter der Liste ins Bild kommt.
  const fuehler = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = registerQuery;
  useEffect(() => {
    const ziel = fuehler.current;
    if (!ziel || !hasNextPage) return;
    const beobachter = new IntersectionObserver((eintraege) => {
      if (eintraege.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage();
    }, { rootMargin: "400px 0px" });
    beobachter.observe(ziel);
    return () => beobachter.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, filtered.length]);

  // Entwürfe sind nur unter „Alle" und „Entwurf" sichtbar, folgen dort aber
  // derselben Suche wie das Register — sonst zeigte der Zähler mehr an, als
  // die Liste tatsächlich hergibt.
  const filteredDrafts = (filter !== "all" && filter !== "draft") ? [] : webDrafts.filter((draft) => {
    const query = suche.toLowerCase();
    const customer = kundeSuche.toLowerCase();
    if (customer && !draft.kunde_name.toLowerCase().includes(customer)) return false;
    if (!query) return true;
    return [draft.document.doc_title || draft.document.subject, draft.kunde_name, draft.projekt_title, KIND_LABEL[draft.kind]]
      .join(" ").toLowerCase().includes(query);
  });

  /**
   * Entwürfe und festgeschriebene Belege in EINER Liste (Benjamin, 16.09.2026).
   *
   * Vorher standen die Entwürfe in einem eigenen Kasten über der Tabelle — man
   * musste wissen, dass es sie gibt, um sie zu finden. Jetzt stehen sie oben in
   * derselben Liste: ohne Nummer, mit der Pille „Entwurf", und ein Klick führt
   * in den Editor statt in den Drawer. Oben, weil sie kein Belegdatum haben,
   * nach dem sie sich einsortieren ließen — und weil das die Belege sind, an
   * denen gerade gearbeitet wird.
   */
  const zeilen: Listenzeile[] = [
    ...filteredDrafts.map((draft) => ({ art: "entwurf" as const, id: draft.id, draft })),
    ...filtered.map((item) => ({ art: "beleg" as const, id: item.id, item })),
  ];

  return (
    <div className="invoice-page">
      <header className="invoice-page-head">
        <div>
          <p className="invoice-kicker">Buchhaltung</p>
          <h1>Rechnungen &amp; Angebote</h1>
          <p>Gemeinsames Register für Ausgangsbelege, Fälligkeiten und Zahlungen.</p>
        </div>
        <div className="invoice-head-actions">
          <button className="invoice-button" type="button" onClick={() => registerQuery.refetch()} disabled={registerQuery.isFetching}>
            {registerQuery.isFetching ? "Aktualisiert …" : "↻ Aktualisieren"}
          </button>
          {/* Anlegen und Einlesen nur am Computer (Benjamin, 20.10.2026). */}
          {!telefon && (
            <>
              <button className="invoice-button" type="button" onClick={() => setCreateKind("invoice")}>Beleg importieren</button>
              <button className="invoice-button" type="button" onClick={() => navigate("/rechnungen/neu?kind=quote")}>+ Angebot erstellen</button>
              <button className="invoice-button primary" type="button" onClick={() => navigate("/rechnungen/neu?kind=invoice")}>+ Rechnung erstellen</button>
            </>
          )}
        </div>
      </header>

      <section className="invoice-kpi-grid" aria-label="Belegkennzahlen">
        <Kpi label="Offene Forderungen" value={euro(summary.open_claims)} meta={`${summary.open_count} Belege`} tone="amber" />
        <Kpi label="Davon überfällig" value={euro(summary.overdue_claims)} meta={`${summary.overdue_count} Belege`} tone={summary.overdue_count ? "red" : undefined} />
        <Kpi label="Angebote draußen" value={euro(summary.offers_outside)} meta={`${summary.offers_count} gestellt`} tone="blue" />
        <Kpi label="Bezahlt gesamt" value={euro(summary.paid_total)} meta={`${summary.paid_count} Rechnungen`} tone="green" />
      </section>

      <nav className="invoice-filter-tabs" aria-label="Register filtern">
        {FILTERS.map(({ value, label }) => (
          <button key={value} type="button" className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>
            {label}<span>{counts[value] || 0}</span>
          </button>
        ))}
      </nav>

      <section className="invoice-register-panel">
        <div className="invoice-toolbar">
          <label className="invoice-search">
            <span aria-hidden="true">⌕</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nummer, Kunde, Projekt oder Status …" />
          </label>
          <label className="invoice-customer-filter">
            <span>Kunde</span>
            <input list="invoice-filter-customers" value={customerFilter} onChange={(event) => setCustomerFilter(event.target.value)} placeholder="Alle Kunden" />
            <datalist id="invoice-filter-customers">{customers.map((customer) => <option key={customer.id} value={customer.name} />)}</datalist>
          </label>
          <span className="invoice-result-count">{filtered.length + filteredDrafts.length} von {total + webDrafts.length}</span>
        </div>

        {registerQuery.isLoading && <div className="invoice-state"><span className="invoice-spinner" />Register wird geladen …</div>}
        {registerQuery.isError && (
          <div className="invoice-state error"><strong>Das Register konnte nicht geladen werden.</strong><button className="invoice-button" onClick={() => registerQuery.refetch()}>Erneut versuchen</button></div>
        )}
        {!registerQuery.isLoading && !registerQuery.isError && (
          <>
            <div className="invoice-table-wrap">
              <table className="invoice-table">
                <thead><tr><th>Nummer</th><th>Status</th><th>Kunde</th><th>Projekt</th><th className="number">Netto</th><th>Fällig</th><th>Datum</th><th>DATEV</th><th /></tr></thead>
                <tbody>
                  {zeilen.map((zeile) => zeile.art === "entwurf" ? (
                    <EntwurfZeile
                      key={zeile.id}
                      draft={zeile.draft}
                      onOpen={() => navigate(`/rechnungen/entwurf/${zeile.draft.id}`)}
                    />
                  ) : (
                    <BelegZeile key={zeile.id} item={zeile.item} onOeffnen={() => setSelected(zeile.item)} />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Die Mobilkarte zeigt dieselben Angaben wie die Tabelle, in drei
                Zeilen: Kennung, Geld, Zusammenhang. Projekt und Fälligkeit
                fehlten hier bisher — also gerade das, wonach man am Telefon
                sucht („Was ist überfällig?"). Der Betrag steht rechts mit
                festen Ziffernbreiten, damit die Spalte beim Scrollen les- und
                vergleichbar bleibt. */}
            <div className="invoice-mobile-list">
              {zeilen.map((zeile) => zeile.art === "entwurf"
                ? <EntwurfKarte key={zeile.id} draft={zeile.draft} onOpen={() => navigate(`/rechnungen/entwurf/${zeile.draft.id}`)} />
                : <BelegKarte key={zeile.id} item={zeile.item} onOeffnen={() => setSelected(zeile.item)} />)}
            </div>
            {zeilen.length === 0 && <div className="invoice-state"><strong>Keine passenden Belege</strong><span>Filter oder Suchbegriff anpassen.</span></div>}
            <div ref={fuehler} className="invoice-nachladen" aria-live="polite">
              {isFetchingNextPage && <><span className="invoice-spinner" />Weitere Belege …</>}
              {!hasNextPage && filtered.length > 0 && <span>{filtered.length} von {total} geladen — das ist alles.</span>}
            </div>
          </>
        )}
      </section>

      <p className="invoice-source-note">Desktop und Web verwenden dieselbe Dokumentvorlage und denselben verbindlichen Nummernkreis.</p>

      {createKind && (
        <InvoiceCreateDialog
          initialKind={createKind} customers={customers}
          onClose={() => setCreateKind(null)}
          onCreated={(item) => { setCreateKind(null); setSelected(item); }}
          onImported={(draftId) => { setCreateKind(null); navigate(`/rechnungen/entwurf/${draftId}`); }}
        />
      )}
      {selected && (
        <InvoiceDetailDrawer
          item={selected} customers={customers} projects={projects}
          onClose={() => setSelected(null)} onUpdated={setSelected}
        />
      )}
    </div>
  );
}

/** Eine Zeile der Liste — Entwurf oder festgeschriebener Beleg. */
type Listenzeile =
  | { art: "entwurf"; id: string; draft: WebInvoiceDraftRecord }
  | { art: "beleg"; id: string; item: InvoiceRegisterItem };

const entwurfTitel = (draft: WebInvoiceDraftRecord) =>
  draft.document.doc_title || draft.document.subject || `${KIND_LABEL[draft.kind]} ohne Titel`;

function BelegZeile({ item, onOeffnen }: { item: InvoiceRegisterItem; onOeffnen: () => void }) {
  const ueberfaellig = isOverdue(item);
  return (
    <tr tabIndex={0} onClick={onOeffnen} onKeyDown={(event) => { if (event.key === "Enter") onOeffnen(); }}>
      <td><strong>{item.nummer}</strong><small>{belegArtLabel(item.kind, item.source_quote_nummer)}{isImported(item) ? " · Importiert" : ""}</small></td>
      <td><span className={`invoice-pill ${statusTone(ueberfaellig ? "ueberfaellig" : item.status)}`}>{ueberfaellig ? "Überfällig" : STATUS_LABEL[item.status]}</span></td>
      <td>{item.kunde_name || "—"}</td>
      <td className="muted">{item.projekt_title || "—"}</td>
      <td className="number"><strong>{euro(item.netto)}</strong></td>
      <td className={ueberfaellig ? "due" : "muted"}>{shortDate(item.faellig_am)}</td>
      <td className="muted">{shortDate(item.issue_date)}</td>
      {/* Ein Angebot geht den Steuerberater nichts an — dort bleibt die Spalte
          bewusst leer statt „noch nicht gesendet" zu behaupten. */}
      <td className={item.an_steuerberater_versendet_at ? "invoice-datev-ok" : "muted"}>
        {item.kind === "quote" ? "" : item.an_steuerberater_versendet_at ? `✓ ${shortDate(item.an_steuerberater_versendet_at)}` : "—"}
      </td>
      <td className="invoice-row-arrow">›</td>
    </tr>
  );
}

/**
 * Ein Entwurf in derselben Tabelle. Keine Nummer (die entsteht erst beim
 * Festschreiben) und kein Belegdatum — dafür der Zeitpunkt der letzten
 * Änderung, denn das ist es, wonach man einen halbfertigen Beleg sucht.
 * Das Löschen steht neben dem Öffnen, nicht darin: ein Knopf im Knopf wäre
 * weder gültig noch bedienbar.
 */
function EntwurfZeile({ draft, onOpen }: { draft: WebInvoiceDraftRecord; onOpen: () => void }) {
  const loeschen = useDeleteWebInvoiceDraft();
  const [fragt, setFragt] = useState(false);
  return (
    <tr className="ist-entwurf" tabIndex={0} onClick={onOpen} onKeyDown={(event) => { if (event.key === "Enter") onOpen(); }}>
      <td><strong>—</strong><small>{belegArtLabel(draft.kind, draft.document?.source_quote_number)}</small></td>
      <td><span className="invoice-pill">Entwurf</span></td>
      <td>{draft.kunde_name || "Kunde noch offen"}</td>
      <td className="muted">{draft.projekt_title || entwurfTitel(draft)}</td>
      <td className="number"><strong>{euro(draft.netto)}</strong></td>
      <td className="muted">—</td>
      <td className="muted">{shortDate(draft.updated_at)}</td>
      <td className="muted" />
      <td className="invoice-row-arrow" onClick={(event) => event.stopPropagation()}>
        {fragt ? (
          <span className="invoice-draft-confirm">
            <button type="button" onClick={() => loeschen.mutate(draft.id)} disabled={loeschen.isPending}>
              {loeschen.isPending ? "…" : "Löschen"}
            </button>
            <button type="button" onClick={() => setFragt(false)}>Behalten</button>
          </span>
        ) : (
          <button className="invoice-icon-button" type="button" onClick={() => setFragt(true)} aria-label={`Entwurf ${entwurfTitel(draft)} verwerfen`}>×</button>
        )}
      </td>
    </tr>
  );
}

function BelegKarte({ item, onOeffnen }: { item: InvoiceRegisterItem; onOeffnen: () => void }) {
  const ueberfaellig = isOverdue(item);
  return (
    <button className="invoice-mobile-card" type="button" onClick={onOeffnen}>
      <span className="invoice-mobile-top">
        <span className="invoice-mobile-nummer">
          <strong>{item.nummer}</strong>
          <small>{belegArtLabel(item.kind, item.source_quote_nummer)}{isImported(item) ? " · Importiert" : ""}</small>
        </span>
        <span className={`invoice-pill ${statusTone(ueberfaellig ? "ueberfaellig" : item.status)}`}>{ueberfaellig ? "Überfällig" : STATUS_LABEL[item.status]}</span>
      </span>
      <span className="invoice-mobile-customer">
        <span>{item.kunde_name || "Ohne Kunde"}</span>
        <strong>{euro(item.netto)}</strong>
      </span>
      <span className="invoice-mobile-meta">
        <span>{item.projekt_title || shortDate(item.issue_date)}</span>
        {item.faellig_am && <span className={ueberfaellig ? "due" : undefined}>fällig {shortDate(item.faellig_am)}</span>}
      </span>
      {item.kind !== "quote" && (
        <span className="invoice-mobile-meta">
          <span>Steuerberater</span>
          <span className={item.an_steuerberater_versendet_at ? "invoice-datev-ok" : undefined}>
            {item.an_steuerberater_versendet_at ? `✓ an DATEV ${shortDate(item.an_steuerberater_versendet_at)}` : "noch nicht gesendet"}
          </span>
        </span>
      )}
    </button>
  );
}

/**
 * Die Entwurfskarte ist ein `div`, keine Karte-als-Knopf: sie trägt das
 * Verwerfen in sich, und eine Karte mit einem Knopf darin ist selbst keiner.
 * Tastaturziel ist der Titel (UI-Konventionen, CLAUDE.md).
 */
function EntwurfKarte({ draft, onOpen }: { draft: WebInvoiceDraftRecord; onOpen: () => void }) {
  const loeschen = useDeleteWebInvoiceDraft();
  const [fragt, setFragt] = useState(false);
  return (
    <div className="invoice-mobile-card ist-entwurf">
      <span className="invoice-mobile-top">
        <button type="button" className="invoice-mobile-titel" onClick={onOpen}>
          <strong>{entwurfTitel(draft)}</strong>
          <small>{belegArtLabel(draft.kind, draft.document?.source_quote_number)} · zuletzt {shortDate(draft.updated_at)}</small>
        </button>
        <span className="invoice-pill">Entwurf</span>
      </span>
      <span className="invoice-mobile-customer">
        <span>{draft.kunde_name || "Kunde noch offen"}</span>
        <strong>{euro(draft.netto)}</strong>
      </span>
      <span className="invoice-mobile-meta">
        <span>{draft.projekt_title || "Ohne Projekt"}</span>
        {fragt ? (
          <span className="invoice-draft-confirm">
            <button type="button" onClick={() => loeschen.mutate(draft.id)} disabled={loeschen.isPending}>
              {loeschen.isPending ? "…" : "Löschen"}
            </button>
            <button type="button" onClick={() => setFragt(false)}>Behalten</button>
          </span>
        ) : (
          <span className="invoice-draft-confirm">
            <button type="button" onClick={() => setFragt(true)}>Verwerfen</button>
          </span>
        )}
      </span>
    </div>
  );
}

function Kpi({ label, value, meta, tone }: { label: string; value: string; meta: string; tone?: string }) {
  return <article className={`invoice-kpi${tone ? ` ${tone}` : ""}`}><span>{label}</span><strong>{value}</strong><small>{meta}</small></article>;
}
